// EXP-03 integration — 2026-11-02 GMT: shared Create Expense modal and mobile action.
// 2026-11-02 GMT: Context-aware Job/Profile Expense launch and post-create refresh event. Original full header retained below.
// frontend/src/components/Layout.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-02
// Updated:      2026-09-24
//
// Purpose:
//               Provides the authenticated Vision Systems application shell.
//
// Role:
//               Owns the viewport-height application layout containing the
//               TopNav, desktop sidebar navigation, mobile navigation state,
//               mobile navigation drawer, independently scrollable page
//               content region, application-level PWA install experience,
//               and per-user/per-device Web Push notification controls.
//
// Lifecycle Status:
//               Active production component.
//
// Change Summary (2026-09-23 - PWA-003 mobile action navigation):
//               • Mounts the tenant-themed mobile bottom navigation at the
//                 authenticated application-shell boundary.
//               • Wires My Day, Home and Jobs to existing routes.
//               • Opens the existing TopNav profile surface through a scoped
//                 application event; no duplicate profile implementation exists.
//               • Wires New Job to the established GlobalJobCreateFlyout event
//                 bridge only on routes where GlobalOverlays is already mounted.
//               • Wires New Quote to the existing QuoteCreateModal through the
//                 authenticated GlobalOverlays boundary.
//               • Wires New Opportunity to the existing OpportunityCreateModal
//                 through the same authenticated overlay boundary.
//               • Hosts the existing CustomerFormWidget in a mobile-safe modal
//                 and wires the New Customer radial action to it.
//               • Wires New Contact to the existing CreateContactModal, adding
//                 global customer selection when no customer is preselected.
//               • Shows unsupported create actions as disabled design-preview
//                 buttons until their existing workflows are wired.
//               • Reserves bottom content space and raises PWA install/push
//                 controls above the fixed mobile navigation and safe area.
//
// Change Summary (2026-09-22 - PWA-002 subscription recovery):
//               • Clears the persisted notification-panel dismissal whenever
//                 the browser has no local PushSubscription.
//               • Ensures the installed PWA exposes Enable notifications after
//                 a stale/expired endpoint has been removed or invalidated.
//               • Preserves the existing server reconciliation, permission,
//                 install, navigation and application-shell behaviour.
//
// Change Summary (2026-09-18 - PWA-002):
//               • Added explicit per-device Web Push notification controls.
//               • Notification panel can now be dismissed independently of the
//                 subscription state; dismissing never disables notifications.
//               • Enabled-state dismissal persists across reloads/browser restarts
//                 and is cleared automatically if the push state changes.
//               • Browser notification permission is requested only after the
//                 user presses Enable notifications.
//               • Uses the authenticated PWA push API; tenant_id and user_id are
//                 never supplied by the browser.
//               • Existing browser subscriptions are reconciled against the
//                 current authenticated Vision user before reuse.
//               • Supports enable, disable, blocked-permission and retry states.
//               • iOS notification controls appear only where Web Push APIs are
//                 actually available (installed Home Screen web app).
//               • Notification UI is suppressed while install guidance or the
//                 mobile navigation drawer is visible.
//
//// Change Summary (2026-09-17):
//               • Added a visible Vision Systems PWA install prompt.
//               • Chromium/Samsung Internet uses the native beforeinstallprompt
//                 flow captured at application bootstrap.
//               • iPhone/iPad receives a non-blocking install guidance banner
//                 directly below TopNav; no fake/native install button is shown.
//               • Prompt is suppressed when Vision is already running in
//                 standalone mode.
//               • "Not now" dismisses the prompt for the current browser session.
//               • Prompt is hidden while the mobile navigation drawer is open.
//               • Samsung Internet receives post-install Home screen guidance
//                 because One UI may install the PWA only into the Apps screen.
//               • No caching, authentication, API or routing behaviour changed.
//
// Change Summary (2026-09-04):
//               • Added the shared 768px mobile breakpoint used by the Portal.
//               • Desktop Sidebar remains unchanged and is rendered only above
//                 the mobile breakpoint.
//               • Added independent mobile-menu state. This is deliberately
//                 separate from the persisted desktop sidebar open/collapsed
//                 preference.
//               • Passes mobile state/menu callbacks to TopNav for the compact
//                 mobile header implementation.
//               • Mounts MobileDrawer only on mobile and connects it to the
//                 Layout-owned mobile-menu state.
//               • Main content consumes the full available width on mobile.
//               • Preserved the existing 100dvh shell, minHeight/minWidth,
//                 overflow and independently scrollable content behaviour.
//               • The existing Layout-level /users/me request is intentionally
//                 retained in this phase; performance cleanup is separate.
//
// Previous Change Summary (2026-08-25):
//               • Corrected application-shell flex sizing for reduced viewport
//                 heights and browser zoom.
//               • Added minHeight: 0 to the content row so it can shrink beneath
//                 the TopNav instead of exceeding the viewport.
//               • Added minWidth: 0 to the main content region to prevent child
//                 content from forcing horizontal layout expansion.
//               • Constrained the complete application shell to the viewport.
//               • Kept scrolling responsibility inside the sidebar and main
//                 content areas rather than on the application shell.
//               • Stabilised the sidebar toggle callback.
//
// Previous Updates:
//               • Uses axiosInstance and /users/me route.
//               • Fixed content padding for full-width pages.
//               • Added DashboardRefreshProvider.
//
// Future Intent:
//               This component remains the authoritative boundary for viewport
//               sizing. Child pages and navigation components must consume the
//               space allocated by this layout rather than independently
//               claiming viewport-height dimensions.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import ExpenseCreateModal from "./ExpenseCreateModal";
import TopNav from "./TopNav";
import MobileDrawer from "./MobileDrawer";
import { VisionMobileNavigation } from "./mobile";
import { useTheme } from "../context/ThemeContext.jsx";
import axiosInstance from "../api/axiosInstance";
import { DashboardRefreshProvider } from "../context/DashboardRefreshContext";

const MOBILE_BREAKPOINT = 768;
const PWA_INSTALL_DISMISSED_KEY = "vision:pwa-install-dismissed";
const PWA_SAMSUNG_INSTALLED_HELP_KEY = "vision:pwa-samsung-installed-help";
const PWA_NOTIFICATION_DISMISSED_KEY = "vision:pwa-notification-dismissed";
const MOBILE_NAVIGATION_BOTTOM_OFFSET =
  "calc(var(--vision-mobile-nav-height) + var(--vision-mobile-nav-safe-bottom) + 12px)";
const MOBILE_QUICK_ACTION_PERMISSIONS = Object.freeze({
  "contact.create": null,
  "expense.create": null,
  "customer.create": null,
  "job.create": null,
  "opportunity.create": null,
  "quote.create": null,
});
const GLOBAL_OVERLAY_EXCLUDED_PATHS = new Set([
  "/dashboard/system",
  "/dashboard/widgets",
  "/dashboard/workflows",
]);

const PUSH_STATUS_CHECKING = "checking";
const PUSH_STATUS_DISABLED = "disabled";
const PUSH_STATUS_ENABLED = "enabled";
const PUSH_STATUS_BLOCKED = "blocked";
const PUSH_STATUS_UNSUPPORTED = "unsupported";
const PUSH_STATUS_ERROR = "error";

function supportsWebPush() {
  return Boolean(
    typeof window !== "undefined" &&
      typeof navigator !== "undefined" &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
  );
}

function urlBase64ToUint8Array(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);

  for (let index = 0; index < rawData.length; index += 1) {
    output[index] = rawData.charCodeAt(index);
  }

  return output;
}

function pushDeviceLabel() {
  if (typeof navigator === "undefined") {
    return "Vision browser";
  }

  const userAgent = navigator.userAgent || "";

  if (/SamsungBrowser/i.test(userAgent)) {
    return "Samsung Internet";
  }

  if (/CriOS/i.test(userAgent)) {
    return "Chrome on iPhone/iPad";
  }

  if (/iPad|iPhone|iPod/i.test(userAgent)) {
    return "Vision on iPhone/iPad";
  }

  if (/Android/i.test(userAgent) && /Chrome/i.test(userAgent)) {
    return "Chrome on Android";
  }

  if (/Edg/i.test(userAgent)) {
    return "Microsoft Edge";
  }

  if (/Chrome/i.test(userAgent)) {
    return "Google Chrome";
  }

  if (/Firefox/i.test(userAgent)) {
    return "Firefox";
  }

  if (/Safari/i.test(userAgent)) {
    return "Safari";
  }

  return "Vision browser";
}

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return false;
    }

    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const apply = () => {
      setIsMobile(mediaQuery.matches);
    };

    apply();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", apply);
    } else {
      mediaQuery.addListener(apply);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", apply);
      } else {
        mediaQuery.removeListener(apply);
      }
    };
  }, [breakpoint]);

  return isMobile;
}

function isStandalonePwa() {
  if (typeof window === "undefined") return false;

  const displayModeStandalone =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches;

  const iosStandalone =
    typeof navigator !== "undefined" &&
    navigator.standalone === true;

  return Boolean(displayModeStandalone || iosStandalone);
}

function isAppleMobileDevice() {
  if (typeof navigator === "undefined") return false;

  const userAgent = navigator.userAgent || "";
  const platform = navigator.platform || "";

  return (
    /iPad|iPhone|iPod/i.test(userAgent) ||
    (platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function isSamsungInternetBrowser() {
  if (typeof navigator === "undefined") return false;

  return /SamsungBrowser/i.test(navigator.userAgent || "");
}

const PwaInstallPrompt = ({ isMobile, theme, suppressed, onVisibilityChange }) => {
  const [runtimeReady, setRuntimeReady] = useState(false);
  const [installAvailable, setInstallAvailable] = useState(() =>
    Boolean(
      typeof window !== "undefined" &&
        window.__visionPwaInstallPrompt
    )
  );
  const [installed, setInstalled] = useState(() => isStandalonePwa());
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return false;
    }

    return (
      window.sessionStorage.getItem(PWA_INSTALL_DISMISSED_KEY) === "1"
    );
  });
  const [installing, setInstalling] = useState(false);
  const [showSamsungInstalledHelp, setShowSamsungInstalledHelp] = useState(() => {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return false;
    }

    return (
      window.sessionStorage.getItem(PWA_SAMSUNG_INSTALLED_HELP_KEY) === "1"
    );
  });
  const [iconUrl, setIconUrl] = useState(null);

  const appleMobile = useMemo(() => isAppleMobileDevice(), []);
  const samsungInternet = useMemo(() => isSamsungInternetBrowser(), []);

  useEffect(() => {
    let cancelled = false;

    fetch("/api/pwa/manifest.webmanifest", {
      method: "GET",
      credentials: "same-origin",
      cache: "no-store",
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Manifest unavailable (${response.status})`);
        }

        const manifest = await response.json();

        if (cancelled) return;

        const icons = Array.isArray(manifest?.icons) ? manifest.icons : [];
        const preferredIcon =
          icons.find(
            (icon) =>
              icon?.purpose === "any" &&
              String(icon?.sizes || "").includes("192x192")
          ) ||
          icons.find((icon) => icon?.purpose === "any") ||
          icons[0] ||
          null;

        setIconUrl(preferredIcon?.src || null);
        setRuntimeReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setRuntimeReady(false);
          setIconUrl(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleInstallAvailable = () => {
      setInstallAvailable(Boolean(window.__visionPwaInstallPrompt));
    };

    const handleInstalled = () => {
      window.__visionPwaInstallPrompt = null;
      setInstallAvailable(false);
      setInstalled(true);

      if (samsungInternet) {
        try {
          window.sessionStorage.setItem(
            PWA_SAMSUNG_INSTALLED_HELP_KEY,
            "1"
          );
        } catch (_) {
          // Session storage is optional; local state still shows the guidance.
        }

        setShowSamsungInstalledHelp(true);
      }
    };

    window.addEventListener(
      "vision:pwa-install-available",
      handleInstallAvailable
    );
    window.addEventListener("vision:pwa-installed", handleInstalled);
    window.addEventListener("appinstalled", handleInstalled);

    handleInstallAvailable();

    return () => {
      window.removeEventListener(
        "vision:pwa-install-available",
        handleInstallAvailable
      );
      window.removeEventListener("vision:pwa-installed", handleInstalled);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, [samsungInternet]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(display-mode: standalone)");

    const handleDisplayModeChange = () => {
      if (isStandalonePwa()) {
        setInstalled(true);
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleDisplayModeChange);
    } else {
      mediaQuery.addListener(handleDisplayModeChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleDisplayModeChange);
      } else {
        mediaQuery.removeListener(handleDisplayModeChange);
      }
    };
  }, []);

  const dismiss = useCallback(() => {
    try {
      window.sessionStorage.setItem(PWA_INSTALL_DISMISSED_KEY, "1");
    } catch (_) {
      // Session storage is optional; local state still dismisses the prompt.
    }

    setDismissed(true);
  }, []);

  const dismissSamsungInstalledHelp = useCallback(() => {
    try {
      window.sessionStorage.removeItem(PWA_SAMSUNG_INSTALLED_HELP_KEY);
    } catch (_) {
      // Session storage is optional; local state still dismisses the guidance.
    }

    setShowSamsungInstalledHelp(false);
  }, []);

  const install = useCallback(async () => {
    const promptEvent = window.__visionPwaInstallPrompt;

    if (!promptEvent || typeof promptEvent.prompt !== "function") {
      setInstallAvailable(false);
      return;
    }

    setInstalling(true);

    try {
      await promptEvent.prompt();

      if (promptEvent.userChoice) {
        const choice = await promptEvent.userChoice;

        if (choice?.outcome === "accepted") {
          setInstalled(true);

          if (samsungInternet) {
            try {
              window.sessionStorage.setItem(
                PWA_SAMSUNG_INSTALLED_HELP_KEY,
                "1"
              );
            } catch (_) {
              // Session storage is optional; local state still shows guidance.
            }

            setShowSamsungInstalledHelp(true);
          }
        } else {
          dismiss();
        }
      } else {
        setInstalled(true);

        if (samsungInternet) {
          try {
            window.sessionStorage.setItem(
              PWA_SAMSUNG_INSTALLED_HELP_KEY,
              "1"
            );
          } catch (_) {
            // Session storage is optional; local state still shows guidance.
          }

          setShowSamsungInstalledHelp(true);
        }
      }
    } catch (error) {
      console.error("Vision PWA install prompt failed:", error);
    } finally {
      window.__visionPwaInstallPrompt = null;
      setInstallAvailable(false);
      setInstalling(false);
    }
  }, [dismiss, samsungInternet]);

  const shouldShowSamsungInstalledHelp =
    runtimeReady &&
    showSamsungInstalledHelp &&
    !suppressed;

  const shouldShowAppleInstall =
    runtimeReady &&
    appleMobile &&
    !installed &&
    !dismissed &&
    !suppressed;

  const shouldShowInstall =
    runtimeReady &&
    !appleMobile &&
    !installed &&
    !dismissed &&
    !suppressed &&
    installAvailable;

  const installUiVisible =
    shouldShowSamsungInstalledHelp ||
    shouldShowAppleInstall ||
    shouldShowInstall;

  useEffect(() => {
    if (typeof onVisibilityChange === "function") {
      onVisibilityChange(installUiVisible);
    }

    return () => {
      if (typeof onVisibilityChange === "function") {
        onVisibilityChange(false);
      }
    };
  }, [installUiVisible, onVisibilityChange]);

  if (
    !shouldShowSamsungInstalledHelp &&
    !shouldShowAppleInstall &&
    !shouldShowInstall
  ) {
    return null;
  }

  const darkMode = Boolean(theme?.darkMode);
  const surface = darkMode ? "#1f2933" : "#ffffff";
  const foreground = darkMode ? "#ffffff" : "#172d41";
  const secondary = darkMode ? "#d4dbe1" : "#52606d";
  const border = darkMode ? "rgba(255,255,255,0.14)" : "rgba(23,45,65,0.16)";
  const primary = theme?.primary_color || "#172d41";

  if (shouldShowAppleInstall) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label="Install Vision Systems on iPhone or iPad"
        style={{
          width: "100%",
          flex: "0 0 auto",
          boxSizing: "border-box",
          borderBottom: `1px solid ${border}`,
          background: surface,
          color: foreground,
          padding: isMobile ? "9px 12px" : "10px 16px",
          fontFamily: theme?.fontBody || "Arial, sans-serif",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 10,
            boxSizing: "border-box",
          }}
        >
          {iconUrl ? (
            <img
              src={iconUrl}
              alt=""
              aria-hidden="true"
              style={{
                width: 36,
                height: 36,
                flex: "0 0 36px",
                borderRadius: 8,
                objectFit: "cover",
              }}
            />
          ) : null}

          <div
            style={{
              minWidth: 0,
              flex: "1 1 auto",
              lineHeight: 1.35,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 800,
                marginBottom: 2,
              }}
            >
              Install Vision Systems
            </div>

            <div
              style={{
                color: secondary,
                fontSize: 12.5,
              }}
            >
              Tap Safari&apos;s <strong>Share</strong> button, choose{" "}
              <strong>Add to Home Screen</strong>, then tap <strong>Add</strong>.
            </div>
          </div>

          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss install guidance"
            style={{
              flex: "0 0 auto",
              minHeight: 36,
              border: `1px solid ${border}`,
              borderRadius: 8,
              background: "transparent",
              color: foreground,
              padding: "7px 10px",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={
        shouldShowSamsungInstalledHelp
          ? "Vision Systems installed"
          : "Install Vision Systems"
      }
      style={{
        position: "fixed",
        zIndex: 1250,
        left: isMobile ? 12 : "auto",
        right: 12,
        bottom: isMobile ? MOBILE_NAVIGATION_BOTTOM_OFFSET : 18,
        width: isMobile ? "auto" : 390,
        maxWidth: isMobile ? "calc(100vw - 24px)" : 390,
        boxSizing: "border-box",
        border: `1px solid ${border}`,
        borderRadius: 16,
        background: surface,
        color: foreground,
        boxShadow: "0 12px 38px rgba(0, 0, 0, 0.22)",
        padding: isMobile ? 14 : 16,
        fontFamily: theme?.fontBody || "Arial, sans-serif",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
        }}
      >
        {iconUrl ? (
          <img
            src={iconUrl}
            alt=""
            aria-hidden="true"
            style={{
              width: 54,
              height: 54,
              flex: "0 0 54px",
              borderRadius: 12,
              objectFit: "cover",
            }}
          />
        ) : null}

        <div style={{ minWidth: 0, flex: "1 1 auto" }}>
          <div
            style={{
              fontSize: 16,
              lineHeight: 1.25,
              fontWeight: 800,
              marginBottom: 5,
            }}
          >
            {shouldShowSamsungInstalledHelp
              ? "Vision Systems installed"
              : "Install Vision Systems"}
          </div>

          {shouldShowSamsungInstalledHelp ? (
            <div
              style={{
                color: secondary,
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              Vision Systems is installed. If it is not on your Home screen,
              open the Apps screen, press and hold <strong>Vision Systems</strong>,
              then choose <strong>Add to Home</strong>.
            </div>
          ) : (
            <div
              style={{
                color: secondary,
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              Install Vision Systems for quicker access and a full-screen app
              experience.
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 8,
          justifyContent: "flex-end",
          marginTop: 14,
        }}
      >
        {shouldShowSamsungInstalledHelp ? (
          <button
            type="button"
            onClick={dismissSamsungInstalledHelp}
            style={{
              minHeight: 42,
              border: "none",
              borderRadius: 9,
              background: primary,
              color: "#ffffff",
              padding: "9px 16px",
              fontSize: 14,
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Got it
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={dismiss}
              style={{
                minHeight: 42,
                border: `1px solid ${border}`,
                borderRadius: 9,
                background: "transparent",
                color: foreground,
                padding: "9px 14px",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                order: isMobile ? 2 : 1,
              }}
            >
              Not now
            </button>

            <button
              type="button"
              onClick={install}
              disabled={installing}
              style={{
                minHeight: 42,
                border: "none",
                borderRadius: 9,
                background: primary,
                color: "#ffffff",
                padding: "9px 16px",
                fontSize: 14,
                fontWeight: 800,
                cursor: installing ? "default" : "pointer",
                opacity: installing ? 0.72 : 1,
                order: isMobile ? 1 : 2,
              }}
            >
              {installing ? "Opening installer..." : "Install"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};


const PwaNotificationControl = ({ isMobile, theme, suppressed }) => {
  const [pushStatus, setPushStatus] = useState(PUSH_STATUS_CHECKING);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  const [permission, setPermission] = useState(() => {
    if (
      typeof Notification !== "undefined" &&
      typeof Notification.permission === "string"
    ) {
      return Notification.permission;
    }

    return "default";
  });

  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined" || !window.localStorage) {
      return false;
    }

    return (
      window.localStorage.getItem(PWA_NOTIFICATION_DISMISSED_KEY) === "1"
    );
  });

  const dismissNotificationControl = useCallback(() => {
    try {
      window.localStorage.setItem(PWA_NOTIFICATION_DISMISSED_KEY, "1");
    } catch (_) {
      // Local storage is optional; local state still dismisses the panel.
    }

    setDismissed(true);
  }, []);

  const refreshStatus = useCallback(async () => {
    if (!supportsWebPush()) {
      setPushStatus(PUSH_STATUS_UNSUPPORTED);
      setMessage("");
      return;
    }

    setPushStatus(PUSH_STATUS_CHECKING);
    setMessage("");

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      const currentPermission = Notification.permission;

      setPermission(currentPermission);

      if (currentPermission === "denied") {
        try {
          window.localStorage.removeItem(PWA_NOTIFICATION_DISMISSED_KEY);
        } catch (_) {
          // Local storage is optional.
        }

        setDismissed(false);
        setPushStatus(PUSH_STATUS_BLOCKED);
        return;
      }

      if (!subscription) {
        try {
          window.localStorage.removeItem(PWA_NOTIFICATION_DISMISSED_KEY);
        } catch (_) {
          // Local storage is optional.
        }

        setDismissed(false);
        setPushStatus(PUSH_STATUS_DISABLED);
        return;
      }

      const response = await axiosInstance.post("/pwa/push/status", {
        endpoint: subscription.endpoint,
      });

      if (response?.data?.subscribed) {
        setPushStatus(PUSH_STATUS_ENABLED);
      } else {
        try {
          window.localStorage.removeItem(PWA_NOTIFICATION_DISMISSED_KEY);
        } catch (_) {
          // Local storage is optional.
        }

        setDismissed(false);
        setPushStatus(PUSH_STATUS_DISABLED);
      }
    } catch (error) {
      console.error("Vision push status check failed:", error);
      setPushStatus(PUSH_STATUS_ERROR);
      setMessage("Vision could not check notification status.");
    }
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const enableNotifications = useCallback(async () => {
    if (!supportsWebPush() || working) {
      return;
    }

    try {
      window.localStorage.removeItem(PWA_NOTIFICATION_DISMISSED_KEY);
    } catch (_) {
      // Local storage is optional.
    }

    setDismissed(false);
    setWorking(true);
    setMessage("");

    let newlyCreatedSubscription = null;

    try {
      let currentPermission = Notification.permission;

      if (currentPermission === "default") {
        currentPermission = await Notification.requestPermission();
      }

      setPermission(currentPermission);

      if (currentPermission !== "granted") {
        setPushStatus(
          currentPermission === "denied"
            ? PUSH_STATUS_BLOCKED
            : PUSH_STATUS_DISABLED
        );
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        const statusResponse = await axiosInstance.post("/pwa/push/status", {
          endpoint: subscription.endpoint,
        });

        if (!statusResponse?.data?.subscribed) {
          await subscription.unsubscribe();
          subscription = null;
        }
      }

      if (!subscription) {
        const keyResponse = await axiosInstance.get("/pwa/push/public-key");
        const publicKey = String(keyResponse?.data?.public_key || "").trim();

        if (!publicKey) {
          throw new Error("Vision Web Push public key is unavailable.");
        }

        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });

        newlyCreatedSubscription = subscription;
      }

      const serialized = subscription.toJSON();

      if (
        !serialized?.endpoint ||
        !serialized?.keys?.p256dh ||
        !serialized?.keys?.auth
      ) {
        throw new Error("Browser returned an incomplete push subscription.");
      }

      await axiosInstance.post("/pwa/push/subscribe", {
        endpoint: serialized.endpoint,
        expirationTime: serialized.expirationTime ?? null,
        keys: {
          p256dh: serialized.keys.p256dh,
          auth: serialized.keys.auth,
        },
        device_label: pushDeviceLabel(),
      });

      setPushStatus(PUSH_STATUS_ENABLED);
      setMessage("Notifications are enabled on this device.");
    } catch (error) {
      console.error("Vision push enable failed:", error);

      if (newlyCreatedSubscription) {
        try {
          await newlyCreatedSubscription.unsubscribe();
        } catch (_) {
          // Best-effort cleanup of a browser subscription that the server
          // failed to persist.
        }
      }

      setPushStatus(PUSH_STATUS_ERROR);

      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        "Vision could not enable notifications.";

      setMessage(String(detail));
    } finally {
      setWorking(false);
    }
  }, [working]);

  const disableNotifications = useCallback(async () => {
    if (!supportsWebPush() || working) {
      return;
    }

    setWorking(true);
    setMessage("");

    let serverCleanupFailed = false;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        try {
          await axiosInstance.post("/pwa/push/unsubscribe", {
            endpoint: subscription.endpoint,
          });
        } catch (error) {
          serverCleanupFailed = true;
          console.error("Vision push server unsubscribe failed:", error);
        }

        try {
          await subscription.unsubscribe();
        } catch (error) {
          console.error("Browser push unsubscribe failed:", error);
          throw error;
        }
      }

      try {
        window.localStorage.removeItem(PWA_NOTIFICATION_DISMISSED_KEY);
      } catch (_) {
        // Local storage is optional.
      }

      setDismissed(false);
      setPushStatus(PUSH_STATUS_DISABLED);
      setMessage(
        serverCleanupFailed
          ? "Notifications are disabled on this device. Server cleanup will be retried automatically if the old endpoint is used."
          : "Notifications are disabled on this device."
      );
    } catch (error) {
      setPushStatus(PUSH_STATUS_ERROR);
      setMessage(
        error?.message || "Vision could not disable notifications on this device."
      );
    } finally {
      setWorking(false);
    }
  }, [working]);

  if (
    suppressed ||
    dismissed ||
    pushStatus === PUSH_STATUS_CHECKING ||
    pushStatus === PUSH_STATUS_UNSUPPORTED
  ) {
    return null;
  }

  const darkMode = Boolean(theme?.darkMode);
  const surface = darkMode ? "#1f2933" : "#ffffff";
  const foreground = darkMode ? "#ffffff" : "#172d41";
  const secondary = darkMode ? "#d4dbe1" : "#52606d";
  const border = darkMode
    ? "rgba(255,255,255,0.14)"
    : "rgba(23,45,65,0.16)";
  const primary = theme?.primary_color || "#172d41";

  const enabled = pushStatus === PUSH_STATUS_ENABLED;
  const blocked =
    pushStatus === PUSH_STATUS_BLOCKED || permission === "denied";
  const errorState = pushStatus === PUSH_STATUS_ERROR;

  let title = "Vision notifications";
  let description =
    "Enable notifications on this device for Vision alerts and updates.";

  if (enabled) {
    description = "Notifications are enabled on this device.";
  } else if (blocked) {
    description =
      "Notifications are blocked in this browser or device settings. Allow notifications for Vision, then check again.";
  } else if (errorState && message) {
    description = message;
  }

  return (
    <div
      role="region"
      aria-live="polite"
      aria-label="Vision notification settings"
      style={{
        position: "fixed",
        zIndex: 1240,
        left: isMobile ? 12 : "auto",
        right: 12,
        bottom: isMobile ? MOBILE_NAVIGATION_BOTTOM_OFFSET : 18,
        width: isMobile ? "auto" : 390,
        maxWidth: isMobile ? "calc(100vw - 24px)" : 390,
        boxSizing: "border-box",
        border: `1px solid ${border}`,
        borderRadius: 16,
        background: surface,
        color: foreground,
        boxShadow: "0 12px 38px rgba(0, 0, 0, 0.22)",
        padding: isMobile ? 14 : 16,
        fontFamily: theme?.fontBody || "Arial, sans-serif",
      }}
    >
      <button
        type="button"
        onClick={dismissNotificationControl}
        aria-label="Dismiss notification settings"
        title="Dismiss"
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          width: 32,
          height: 32,
          minWidth: 32,
          border: "none",
          borderRadius: 8,
          background: "transparent",
          color: secondary,
          fontSize: 22,
          lineHeight: 1,
          fontWeight: 700,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
        }}
      >
        ×
      </button>

      <div
        style={{
          fontSize: 16,
          lineHeight: 1.25,
          fontWeight: 800,
          marginBottom: 5,
          paddingRight: 34,
        }}
      >
        {title}
      </div>

      <div
        style={{
          color: secondary,
          fontSize: 13,
          lineHeight: 1.45,
        }}
      >
        {description}
      </div>

      {message && !errorState && (
        <div
          style={{
            color: secondary,
            fontSize: 12.5,
            lineHeight: 1.4,
            marginTop: 6,
          }}
        >
          {message}
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 8,
          justifyContent: "flex-end",
          marginTop: 14,
        }}
      >
        {enabled ? (
          <button
            type="button"
            onClick={disableNotifications}
            disabled={working}
            style={{
              minHeight: 42,
              border: `1px solid ${border}`,
              borderRadius: 9,
              background: "transparent",
              color: foreground,
              padding: "9px 14px",
              fontSize: 14,
              fontWeight: 700,
              cursor: working ? "default" : "pointer",
              opacity: working ? 0.72 : 1,
            }}
          >
            {working ? "Disabling..." : "Disable notifications"}
          </button>
        ) : blocked ? (
          <button
            type="button"
            onClick={refreshStatus}
            disabled={working}
            style={{
              minHeight: 42,
              border: "none",
              borderRadius: 9,
              background: primary,
              color: "#ffffff",
              padding: "9px 16px",
              fontSize: 14,
              fontWeight: 800,
              cursor: working ? "default" : "pointer",
              opacity: working ? 0.72 : 1,
            }}
          >
            Check again
          </button>
        ) : (
          <>
            {errorState && (
              <button
                type="button"
                onClick={refreshStatus}
                disabled={working}
                style={{
                  minHeight: 42,
                  border: `1px solid ${border}`,
                  borderRadius: 9,
                  background: "transparent",
                  color: foreground,
                  padding: "9px 14px",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: working ? "default" : "pointer",
                  opacity: working ? 0.72 : 1,
                  order: isMobile ? 2 : 1,
                }}
              >
                Retry status
              </button>
            )}

            <button
              type="button"
              onClick={enableNotifications}
              disabled={working}
              style={{
                minHeight: 42,
                border: "none",
                borderRadius: 9,
                background: primary,
                color: "#ffffff",
                padding: "9px 16px",
                fontSize: 14,
                fontWeight: 800,
                cursor: working ? "default" : "pointer",
                opacity: working ? 0.72 : 1,
                order: isMobile ? 1 : 2,
              }}
            >
              {working ? "Enabling..." : "Enable notifications"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expenseCreateOpen, setExpenseCreateOpen] = useState(false);
  const [expenseCreateContext, setExpenseCreateContext] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pwaInstallUiVisible, setPwaInstallUiVisible] = useState(false);

  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();

  const mobileActiveTab = useMemo(() => {
    if (location.pathname === "/dashboard/calendar") return "my-day";
    if (
      location.pathname === "/dashboard/jobs" ||
      location.pathname.startsWith("/dashboard/jobs/")
    ) {
      return "jobs";
    }
    if (location.pathname === "/dashboard") return "home";
    return null;
  }, [location.pathname]);

  const globalCreateAvailable = !GLOBAL_OVERLAY_EXCLUDED_PATHS.has(
    location.pathname
  );

  useEffect(() => {
    if (!isMobile) {
      setMobileMenuOpen(false);
    }
  }, [isMobile]);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((current) => !current);
  }, []);

  const openMobileMenu = useCallback(() => {
    setMobileMenuOpen(true);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const handleMobileNavigation = useCallback(
    (navigationId) => {
      if (navigationId === "my-day") {
        navigate("/dashboard/calendar");
        return;
      }

      if (navigationId === "home") {
        navigate("/dashboard");
        return;
      }

      if (navigationId === "jobs") {
        navigate("/dashboard/jobs");
        return;
      }

      if (navigationId === "profile") {
        window.dispatchEvent(new CustomEvent("vision:open-profile"));
      }
    },
    [navigate]
  );

  const openGlobalJobCreate = useCallback(() => {
    if (typeof window.__GJCF_PREFILL === "function") {
      window.__GJCF_PREFILL({});
      return;
    }

    window.dispatchEvent(
      new CustomEvent("calendar:prefill-job", { detail: {} })
    );
  }, []);

  const openGlobalQuoteCreate = useCallback(() => {
    if (typeof window.__VISION_OPEN_QUOTE_CREATE === "function") {
      window.__VISION_OPEN_QUOTE_CREATE();
      return;
    }

    window.dispatchEvent(new CustomEvent("vision:open-quote-create"));
  }, []);

  const openGlobalOpportunityCreate = useCallback(() => {
    if (typeof window.__VISION_OPEN_OPPORTUNITY_CREATE === "function") {
      window.__VISION_OPEN_OPPORTUNITY_CREATE();
      return;
    }

    window.dispatchEvent(
      new CustomEvent("vision:open-opportunity-create")
    );
  }, []);

  const openGlobalCustomerCreate = useCallback(() => {
    if (typeof window.__VISION_OPEN_CUSTOMER_CREATE === "function") {
      window.__VISION_OPEN_CUSTOMER_CREATE();
      return;
    }

    window.dispatchEvent(new CustomEvent("vision:open-customer-create"));
  }, []);

  const openGlobalExpenseCreate = useCallback(() => {
    setExpenseCreateContext(null);
    setExpenseCreateOpen(true);
  }, []);

  useEffect(() => {
    const openExpense = (event) => {
      const context = event.detail || {};
      setExpenseCreateContext({
        customerId: context.customerId || null,
        jobId: context.jobId || null,
      });
      setExpenseCreateOpen(true);
    };
    window.addEventListener("vision:open-expense-create", openExpense);
    return () => window.removeEventListener("vision:open-expense-create", openExpense);
  }, []);

  const openGlobalContactCreate = useCallback(() => {
    if (typeof window.__VISION_OPEN_CONTACT_CREATE === "function") {
      window.__VISION_OPEN_CONTACT_CREATE();
      return;
    }

    window.dispatchEvent(new CustomEvent("vision:open-contact-create"));
  }, []);

  const handlePwaInstallVisibilityChange = useCallback((visible) => {
    setPwaInstallUiVisible(Boolean(visible));
  }, []);

  return (
    <DashboardRefreshProvider>
      <div
        style={{
          width: "100%",
          height: "100dvh",
          maxHeight: "100dvh",

          display: "flex",
          flexDirection: "column",

          overflow: "hidden",

          boxSizing: "border-box",
        }}
      >
        <TopNav
          isMobile={isMobile}
          mobileMenuOpen={mobileMenuOpen}
          onOpenMobileMenu={openMobileMenu}
          onCloseMobileMenu={closeMobileMenu}
        />

        {isMobile && (
          <MobileDrawer
            open={mobileMenuOpen}
            onClose={closeMobileMenu}
          />
        )}

        <PwaInstallPrompt
          isMobile={isMobile}
          theme={theme}
          suppressed={isMobile && mobileMenuOpen}
          onVisibilityChange={handlePwaInstallVisibilityChange}
        />

        <PwaNotificationControl
          isMobile={isMobile}
          theme={theme}
          suppressed={
            (isMobile && mobileMenuOpen) ||
            pwaInstallUiVisible
          }
        />

        <div
          style={{
            display: "flex",

            flex: "1 1 auto",

            /*
             * Critical for nested flex layouts.
             *
             * Without minHeight: 0, this flex child may retain the intrinsic
             * height required by its descendants instead of shrinking to the
             * remaining viewport height below TopNav.
             */
            minHeight: 0,

            width: "100%",

            overflow: "hidden",

            boxSizing: "border-box",
          }}
        >
          {!isMobile && (
            <Sidebar
              isOpen={sidebarOpen}
              toggleSidebar={toggleSidebar}
            />
          )}

          <main
            style={{
              flex: "1 1 auto",

              /*
               * Required so large page content cannot force the flex item
               * wider or taller than the application-shell allocation.
               */
              minWidth: 0,
              minHeight: 0,

              width: isMobile ? "100%" : undefined,

              overflowY: "auto",
              overflowX: "hidden",

              padding: isMobile
                ? `10px 0 ${MOBILE_NAVIGATION_BOTTOM_OFFSET}`
                : "10px 0",

              backgroundColor: theme.darkMode
                ? "#222"
                : "#f5f5f5",

              fontFamily: theme.fontBody || "Arial, sans-serif",

              boxSizing: "border-box",

              WebkitOverflowScrolling: "touch",
            }}
          >
            {children}
          </main>
        </div>

        <ExpenseCreateModal
          open={expenseCreateOpen}
          context={expenseCreateContext}
          onClose={() => setExpenseCreateOpen(false)}
          onCreated={(created) => window.dispatchEvent(new CustomEvent("vision:expense-created", { detail: created }))}
        />

        {isMobile && !mobileMenuOpen && (
          <VisionMobileNavigation
            activeTab={mobileActiveTab}
            onNavigate={handleMobileNavigation}
            openExpenseCreate={globalCreateAvailable ? openGlobalExpenseCreate : null}
            openContactCreate={
              globalCreateAvailable ? openGlobalContactCreate : null
            }
            openCustomerCreate={
              globalCreateAvailable ? openGlobalCustomerCreate : null
            }
            openJobCreate={globalCreateAvailable ? openGlobalJobCreate : null}
            openOpportunityCreate={
              globalCreateAvailable ? openGlobalOpportunityCreate : null
            }
            openQuoteCreate={globalCreateAvailable ? openGlobalQuoteCreate : null}
            permissionByAction={MOBILE_QUICK_ACTION_PERMISSIONS}
          />
        )}
      </div>
    </DashboardRefreshProvider>
  );
};

export default Layout;
